import React, { useState, useEffect } from 'react';
import PaperSizePanel from './UI/PaperSizePanel';
import { PAPER_SIZES } from '../constants';

const DuplexPrinterIcon = () => (
  <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="printer-svg">
    {/* Printer body */}
    <rect x="5" y="50" width="90" height="42" rx="4" className="printer-body" />
    {/* Auto-feed input tray on top */}
    <path d="M 30 50 L 40 15 L 75 15 L 68 50 Z" className="printer-tray" />
    {/* Paper sheets in input tray */}
    <line x1="43" y1="21" x2="72" y2="21" className="printer-paper" />
    <line x1="42" y1="27" x2="71" y2="27" className="printer-paper" />
    <line x1="41" y1="33" x2="70" y2="33" className="printer-paper" />
    {/* Output slot on body */}
    <rect x="10" y="79" width="48" height="5" rx="2" className="printer-slot" />
    {/* Status light */}
    <circle cx="78" cy="65" r="4" className="printer-light" />
  </svg>
);

const NonDuplexPrinterIcon = () => (
  <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="printer-svg">
    {/* Printer body */}
    <rect x="5" y="35" width="90" height="55" rx="4" className="printer-body" />
    {/* Recessed top tray — inset into the top surface */}
    <rect x="18" y="35" width="58" height="14" rx="2" className="printer-recess" />
    {/* Paper sheets sitting in the recessed tray */}
    <line x1="22" y1="41" x2="72" y2="41" className="printer-paper" />
    <line x1="22" y1="46" x2="72" y2="46" className="printer-paper" />
    {/* Output slot on body */}
    <rect x="10" y="76" width="48" height="5" rx="2" className="printer-slot" />
    {/* Status light */}
    <circle cx="78" cy="60" r="4" className="printer-light" />
  </svg>
);

const ExportScreen = ({
  isOpen, onClose, onExport,
  sourcePageCount, currentSignatures, sheetsPerSignature,
  outputPaperSize, setOutputPaperSize,
  customPaperWidth, setCustomPaperWidth,
  customPaperHeight, setCustomPaperHeight,
  useMillimeters, setUseMillimeters,
  isDuplex, setIsDuplex,
}) => {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsClosing(true);
        setTimeout(() => {
          setIsClosing(false);
          onClose();
        }, 250);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 250);
  };

  const handleExport = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onExport();
    }, 250);
  };

  if (!isOpen) return null;

  const totalSheets = currentSignatures
    ? currentSignatures.reduce((sum, sig) => sum + sig.length, 0)
    : null;

  return (
    <div className={`export-screen-overlay${isClosing ? ' closing' : ''}`}>
      <div className="export-screen-panel">
        <button className="close-button" onClick={handleClose}>&times;</button>
        <h3 className="export-screen-title">Print/Page Setup</h3>
        <div className="export-screen-settings">
          {/* Paper size — v3.0.2 */}
          <div className="export-setting-group">
            <span className="export-setting-label">Paper Size</span>
            <PaperSizePanel
              outputPaperSize={outputPaperSize}
              setOutputPaperSize={setOutputPaperSize}
              customPaperWidth={customPaperWidth}
              setCustomPaperWidth={setCustomPaperWidth}
              customPaperHeight={customPaperHeight}
              setCustomPaperHeight={setCustomPaperHeight}
              useMillimeters={useMillimeters}
              setUseMillimeters={setUseMillimeters}
              PAPER_SIZES={PAPER_SIZES}
            />
          </div>

          {/* Duplex / Non-Duplex toggle — v3.0.3 */}
          <div className="export-setting-group">
            <span className="export-setting-label">Print Mode</span>
            <div className="duplex-toggle">
              <div className="duplex-toggle-buttons">
                <button
                  type="button"
                  className={`duplex-toggle-btn${isDuplex ? ' active' : ''}`}
                  onClick={() => setIsDuplex(true)}
                >
                  Duplex
                </button>
                <button
                  type="button"
                  className={`duplex-toggle-btn${!isDuplex ? ' active' : ''}`}
                  onClick={() => setIsDuplex(false)}
                >
                  Non-Duplex
                </button>
              </div>
              <div className="printer-illustration">
                {isDuplex ? <DuplexPrinterIcon /> : <NonDuplexPrinterIcon />}
              </div>
              <p className="duplex-hint">
                {isDuplex
                  ? "Your printer handles both sides automatically. You'll get a single PDF ready to send straight to print."
                  : "Your printer will print one side at a time. You'll get two PDFs — print the odd pages first, reload the paper, then print the even pages."
                }
              </p>
            </div>
          </div>
        </div>

        {sourcePageCount !== null && totalSheets !== null && (
          <div className="export-summary">
            <p>
              {sourcePageCount} pages &rarr; {totalSheets} sheets &middot; {sheetsPerSignature} sheets/signature
            </p>
          </div>
        )}
        <button className="primary-action" onClick={handleExport}>
          Export Booklet
        </button>
      </div>
    </div>
  );
};

export default ExportScreen;
